import React, { useEffect, useRef, useState } from 'react';
import Card from '@/components/ui/Card';
import PageHeader from '@/components/layout/PageHeader';
import { dashboardAPI } from '@/services/api';
import { ManagerDashboardStats, StaffDashboardRole, StaffDashboardStats } from '@/types';
import { useView } from '@/context/ViewContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useRealtime } from '@/context/RealtimeContext';
import CoachDashboardView from '@/components/dashboard/coach/CoachDashboardView';
import ManagerDashboardView from '@/components/dashboard/manager/ManagerDashboardView';
import MedicalDashboardView from '@/components/dashboard/medical/MedicalDashboardView';
import ScoutDashboardView from '@/components/dashboard/scout/ScoutDashboardView';
import { LoadedStaffDashboard } from './DashboardPage/types';
import { getDashboardTitle, getDashboardSubtitle } from './DashboardPage/helpers';
import DelayedLoading from '@/components/ui/DelayedLoading';
import { SkeletonBlock } from '@/components/ui/LoadingSkeleton';

const isStaffDashboardRole = (role: string): role is StaffDashboardRole =>
  role === 'manager' || role === 'coach' || role === 'medical' || role === 'scout';

interface DashboardLoadingCopy {
  briefing: string;
  metrics: string;
}

const getDashboardLoadingCopy = (role: string): DashboardLoadingCopy => {
  switch (role) {
    case 'manager':
      return { briefing: 'Preparing match readiness briefing', metrics: 'Loading squad metrics' };
    case 'coach':
      return { briefing: 'Preparing training week pulse', metrics: 'Loading training metrics' };
    case 'medical':
      return { briefing: 'Preparing squad availability briefing', metrics: 'Loading medical metrics' };
    case 'scout':
      return { briefing: 'Preparing player intelligence briefing', metrics: 'Loading scouting metrics' };
    default:
      return { briefing: 'Preparing dashboard', metrics: 'Loading dashboard metrics' };
  }
};

const isManagerDashboardStats = (stats: StaffDashboardStats): stats is ManagerDashboardStats =>
  typeof stats.recentReports === 'number';

const adaptDashboardStats = (
  role: StaffDashboardRole,
  stats: StaffDashboardStats,
): LoadedStaffDashboard => {
  switch (role) {
    case 'medical':
      if ('recoveryPlayers' in stats) return { role, stats };
      break;
    case 'scout':
      if ('averageRating' in stats) return { role, stats };
      break;
    case 'coach':
      if (isManagerDashboardStats(stats)) return { role, stats };
      break;
    case 'manager':
      if (isManagerDashboardStats(stats)) return { role, stats };
      break;
  }

  throw new Error(`Invalid ${role} dashboard response`);
};

const DashboardPage: React.FC = () => {
  const { currentViewRole } = useView();
  const { currentWorkspace } = useWorkspace();
  const { lastEvent } = useRealtime();
  const [dashboard, setDashboard] = useState<LoadedStaffDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSequenceRef = useRef(0);
  const latestSuccessfulRequestRef = useRef(0);
  const latestCoreRequestRef = useRef(0);
  const contextRef = useRef({ key: '', version: 0 });
  const [retryVersion, setRetryVersion] = useState(0);
  const currentViewRoleRef = useRef(currentViewRole);
  const lastHandledEventRef = useRef(lastEvent);
  currentViewRoleRef.current = currentViewRole;
  const contextKey = `${currentViewRole}:${currentWorkspace?.id ?? ''}`;
  const dashboardTitle = getDashboardTitle(currentViewRole);
  const loadingCopy = getDashboardLoadingCopy(currentViewRole);
  if (contextRef.current.key !== contextKey) {
    contextRef.current = { key: contextKey, version: contextRef.current.version + 1 };
    latestSuccessfulRequestRef.current = 0;
  }

  useEffect(() => {
    let active = true;
    const contextVersion = contextRef.current.version;
    const requestSequence = ++requestSequenceRef.current;
    latestCoreRequestRef.current = requestSequence;

    if (!isStaffDashboardRole(currentViewRole)) {
      setDashboard(null);
      setError(`Unsupported dashboard role: ${currentViewRole}`);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await dashboardAPI.getForRole(currentViewRole);
        const adaptedDashboard = adaptDashboardStats(currentViewRole, response.data);
        if (active
          && contextVersion === contextRef.current.version
          && requestSequence > latestSuccessfulRequestRef.current) {
          latestSuccessfulRequestRef.current = requestSequence;
          setDashboard(adaptedDashboard);
        }
      } catch (error: unknown) {
        if (active
          && contextVersion === contextRef.current.version
          && requestSequence > latestSuccessfulRequestRef.current) {
          setDashboard(null);
          setError(error instanceof Error ? error.message : 'Failed to load dashboard');
        }
      } finally {
        if (active
          && contextVersion === contextRef.current.version
          && requestSequence === latestCoreRequestRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [currentViewRole, currentWorkspace?.id, retryVersion]);

  useEffect(() => {
    if (lastEvent === lastHandledEventRef.current) return;
    lastHandledEventRef.current = lastEvent;

    if (!lastEvent) {
      return;
    }
    const legacyReportEvent = /^(scouting|performance|medical|training)_report\.(created|updated|deleted)$/.test(lastEvent.type);
    const payload = lastEvent.payload;
    const normalizedReportEvent = /^report\.(created|updated|deleted)$/.test(lastEvent.type)
      && typeof payload === 'object'
      && payload !== null
      && 'domain' in payload
      && payload.domain === 'reports';
    if (!legacyReportEvent && !normalizedReportEvent) {
      return;
    }

    const role = currentViewRoleRef.current;
    if (!isStaffDashboardRole(role)) return;

    const contextVersion = contextRef.current.version;
    const requestSequence = ++requestSequenceRef.current;

    (async () => {
      try {
        const response = await dashboardAPI.getForRole(role);
        const adaptedDashboard = adaptDashboardStats(role, response.data);
        if (contextVersion === contextRef.current.version
          && requestSequence > latestSuccessfulRequestRef.current) {
          latestSuccessfulRequestRef.current = requestSequence;
          setDashboard(adaptedDashboard);
          setError(null);
          setLoading(false);
        }
      } catch {
        // Realtime refresh is optional; retain the last successful dashboard.
      }
    })();
  }, [lastEvent]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={dashboardTitle} subtitle={getDashboardSubtitle(currentViewRole)} />
        <DelayedLoading loading={loading}>
          <div role="status" aria-live="polite" aria-label="Loading dashboard" className="space-y-6">

            <div data-loading-section className="rounded-2xl border-l-[5px] border-l-club-gold-400 bg-club-navy-950 p-5 motion-reduce:animate-none sm:p-6">
              <p className="text-sm font-medium text-club-navy-100">{loadingCopy.briefing}</p>
              <SkeletonBlock className="mt-4 h-8 w-2/3 bg-club-navy-800" />
              <SkeletonBlock className="mt-3 h-4 w-1/2 bg-club-navy-800" />
            </div>

            <div data-loading-section className="motion-reduce:animate-none">
              <p className="sr-only">{loadingCopy.metrics}</p>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[1, 2, 3, 4].map(item => <SkeletonBlock key={item} className="h-24 rounded-xl" />)}
              </div>
            </div>

            <div data-loading-section className="grid grid-cols-1 gap-6 motion-reduce:animate-none lg:grid-cols-3">
              <p className="sr-only">Loading priority sections</p>
              <SkeletonBlock className="h-52 rounded-xl lg:col-span-2" />
              <SkeletonBlock className="h-52 rounded-xl" />
            </div>
          </div>
        </DelayedLoading>
      </div>
    );
  }

  const renderRoleSpecificDashboard = () => {
    if (!dashboard) return null;

    switch (dashboard.role) {
      case 'medical':
        return (
          <MedicalDashboardView
            data={dashboard.stats}
            loading={loading}
            error={error}
          />
        );
      case 'scout':
        return (
          <ScoutDashboardView
            data={dashboard.stats}
            loading={loading}
            error={error}
          />
        );
      case 'manager':
        return <ManagerDashboardView data={dashboard.stats} />;
      case 'coach':
        return <CoachDashboardView data={dashboard.stats} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title={dashboardTitle} subtitle={getDashboardSubtitle(currentViewRole)} />

      {error && (
        <Card className="p-0">
          <div
            role="alert"
            aria-label={`${dashboardTitle.replace(' Dashboard', '')} dashboard unavailable`}
            className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/40"
          >
            <h2 className="text-base font-semibold text-red-900 dark:text-red-100">
              We couldn't load the {dashboardTitle}
            </h2>
            <p className="mt-1 text-sm text-red-800 dark:text-red-200">
              Try again. Your selected role and workspace are unchanged.
            </p>
            <button
              type="button"
              onClick={() => setRetryVersion(version => version + 1)}
              className="mt-3 inline-flex min-h-11 items-center rounded-md border border-red-300 px-4 text-sm font-medium text-red-900 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 dark:border-red-700 dark:text-red-100 dark:hover:bg-red-900/60 motion-reduce:transition-none"
            >
              Retry dashboard
            </button>
          </div>
        </Card>
      )}

      {renderRoleSpecificDashboard()}
    </div>
  );
};

export default DashboardPage;
